import { type FC, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Calendar, MapPin, Clock, Sparkles, ChevronRight, ListOrdered } from 'lucide-react';
import { Card, CardContent, Badge, Button } from '@/components/ui';
import { supabase } from '@/lib/api/supabase';
import type { SpecialEvent, DbSpecialEvent, Addon, DbAddon, AddonWaitlistEntry } from '@/types';
import { dbToSpecialEvent, dbToAddon } from '@/types';
import { useAddonWaitlist } from '@/hooks/data/useAddonWaitlist';
import { useAuth } from '@/hooks';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';
import { ClosedNotice } from '@/components/shared';

interface SpecialEventWithAddon extends SpecialEvent {
  linkedAddon?: Addon | null;
}

/**
 * Special Events listing page
 * Displays all published special events as informational cards
 */
export const SpecialEventsPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isLoggedIn = !!user;

  const [events, setEvents] = useState<SpecialEventWithAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [waitlistEntries, setWaitlistEntries] = useState<Record<string, AddonWaitlistEntry>>({});
  const [waitlistError, setWaitlistError] = useState<string | null>(null);
  const { joinWaitlist, leaveWaitlist, getUserWaitlistEntries, loading: waitlistLoading } = useAddonWaitlist();

  useEffect(() => {
    let cancelled = false;

    const fetchEvents = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all published and visible special events with linked addon data
        const { data, error: fetchError } = await supabase
          .from('special_events')
          .select('*, linked_addon:addons!linked_addon_id(*)')
          .eq('status', 'published')
          .eq('visible', true)
          .order('event_date', { ascending: true });

        if (fetchError) {
          throw fetchError;
        }

        if (data && !cancelled) {
          const mappedEvents: SpecialEventWithAddon[] = data.map((item: any) => ({
            ...dbToSpecialEvent(item as DbSpecialEvent),
            linkedAddon: item.linked_addon ? dbToAddon(item.linked_addon as DbAddon) : null,
          }));
          setEvents(mappedEvents);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch special events:', err);
          setError(err instanceof Error ? err.message : 'Failed to load special events');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchEvents();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch user's addon waitlist entries when auth state is resolved
  useEffect(() => {
    let cancelled = false;

    const fetchWaitlistEntries = async (): Promise<void> => {
      if (!user) return;

      const entries = await getUserWaitlistEntries();

      if (!cancelled) {
        const entriesMap: Record<string, AddonWaitlistEntry> = {};
        entries.forEach(e => { entriesMap[e.addonId] = e; });
        setWaitlistEntries(entriesMap);
      }
    };

    void fetchWaitlistEntries();

    return () => {
      cancelled = true;
    };
  }, [user, getUserWaitlistEntries]);

  const handleJoinWaitlist = async (addonId: string, e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    setWaitlistError(null);
    const result = await joinWaitlist(addonId);
    if (result.success) {
      // Re-fetch entries
      const entries = await getUserWaitlistEntries();
      const entriesMap: Record<string, AddonWaitlistEntry> = {};
      entries.forEach(entry => { entriesMap[entry.addonId] = entry; });
      setWaitlistEntries(entriesMap);
    } else {
      setWaitlistError(result.error ?? 'Failed to join waitlist. Please try again.');
    }
  };

  const handleLeaveWaitlist = async (addonId: string, e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    setWaitlistError(null);
    const result = await leaveWaitlist(addonId);
    if (result.success) {
      setWaitlistEntries(prev => {
        const next = { ...prev };
        delete next[addonId];
        return next;
      });
    } else {
      setWaitlistError(result.error ?? 'Failed to leave waitlist. Please try again.');
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (startTime: string | null, endTime: string | null): string => {
    if (!startTime) return '';
    const start = startTime;
    const end = endTime ? ` - ${endTime}` : '';
    return `${start}${end}`;
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-white">Loading special events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="rounded-lg bg-red-900/40 border border-red-500/40 p-6 text-red-200 backdrop-blur-md shadow-lg inline-block max-w-md">
              <p className="font-semibold mb-2">Error loading special events</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[300px] flex items-center justify-center py-12">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-viking mb-4 drop-shadow-lg">
              Special Events
            </h1>
            <p className="text-xl font-medium drop-shadow-md">
              Exclusive gatherings and experiences at Cactus Cup
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 max-w-5xl">
        {events.length === 0 ? (
          <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
            <CardContent className="py-12">
              <div className="text-center">
                <Sparkles className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                <h2 className="text-2xl font-viking text-white mb-2">No Special Events Yet</h2>
                <p className="text-orange-200">
                  Check back soon for upcoming special events and exclusive gatherings!
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {events.map((event) => {
              const isSoldOut = event.linkedAddon
                && event.linkedAddon.hasInventory
                && event.linkedAddon.stockQuantity !== null
                && event.linkedAddon.stockQuantity <= 0;

              const spotsRemaining = event.linkedAddon?.hasInventory
                ? event.linkedAddon.stockQuantity
                : null;

              return (
                <div
                  key={event.id}
                  className="block group cursor-pointer"
                  onClick={() => navigate('/special-events/' + event.slug)}
                >
                  <Card className={`bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/20 ${isSoldOut ? 'border-red-500/50 opacity-80' : 'border-turquoise-500 hover:border-orange-500'}`}>
                    <div className="flex flex-col md:flex-row">
                      {/* Hero Image */}
                      {event.heroImageUrl && (
                        <div className="relative h-48 md:h-auto md:w-64 lg:w-80 flex-shrink-0 overflow-hidden">
                          <img
                            src={event.heroImageUrl}
                            alt={event.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-turquoise-700/50 hidden md:block" />
                          {isSoldOut && (
                            <div className="absolute top-3 left-3">
                              <Badge variant="destructive" className="text-sm px-3 py-1 font-bold">
                                Sold Out
                              </Badge>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Content */}
                      <CardContent className="flex-1 p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h2 className="text-2xl md:text-3xl font-viking text-white group-hover:text-orange-300 transition-colors">
                                {event.title}
                              </h2>
                              {isSoldOut && !event.heroImageUrl && (
                                <Badge variant="destructive" className="text-xs">
                                  Sold Out
                                </Badge>
                              )}
                              {!isSoldOut && spotsRemaining !== null && spotsRemaining <= 10 && (
                                <Badge variant="warning" className="text-xs">
                                  {spotsRemaining} left
                                </Badge>
                              )}
                            </div>
                            {event.subtitle && (
                              <p className="text-orange-200 text-lg mt-1">{event.subtitle}</p>
                            )}

                            {/* Quick Info */}
                            <div className="flex flex-wrap gap-4 mt-4 text-sm">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-orange-500" />
                                <span className="text-white">{formatDate(event.date)}</span>
                              </div>
                              {(event.startTime || event.endTime) && (
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-orange-500" />
                                  <span className="text-white">{formatTime(event.startTime, event.endTime)}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-orange-500" />
                                <span className="text-white">{event.venue || event.location}</span>
                              </div>
                            </div>

                            {/* Description */}
                            <p className="text-white/80 mt-4">
                              {event.description.split('\n\n')[0]}
                            </p>

                            {/* Waitlist UI for sold out events with linked addon */}
                            {isSoldOut && event.linkedAddonId && (
                              <div className="mt-3">
                                {waitlistEntries[event.linkedAddonId] ? (
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm text-orange-200">
                                      <ListOrdered className="h-4 w-4 inline mr-1" />
                                      You're on the waitlist
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-white border-white/40 hover:bg-white/20 text-xs"
                                      onClick={(e) => handleLeaveWaitlist(event.linkedAddonId!, e)}
                                      disabled={waitlistLoading}
                                    >
                                      Leave
                                    </Button>
                                  </div>
                                ) : REGISTRATIONS_CLOSED ? (
                                  <ClosedNotice className="text-sm text-orange-200" />
                                ) : isLoggedIn ? (
                                  <Button
                                    size="sm"
                                    className="bg-orange-500 hover:bg-orange-600 text-white text-xs"
                                    onClick={(e) => handleJoinWaitlist(event.linkedAddonId!, e)}
                                    disabled={waitlistLoading}
                                  >
                                    <ListOrdered className="h-3 w-3 mr-1" />
                                    Join Waitlist
                                  </Button>
                                ) : (
                                  <p className="text-sm text-orange-200">
                                    <Link
                                      to="/login"
                                      state={{ returnTo: location.pathname }}
                                      className="underline hover:text-white"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Log in
                                    </Link>
                                    {' '}to join the waitlist
                                  </p>
                                )}
                                {waitlistError && (
                                  <p className="mt-2 text-sm text-red-300">{waitlistError}</p>
                                )}
                              </div>
                            )}

                            {/* Register Button - hidden when sold out or while registrations are closed */}
                            {!isSoldOut && !REGISTRATIONS_CLOSED && event.registerButtonText && event.registerButtonUrl && (
                              event.registerButtonUrl.startsWith('http') ? (
                                <a
                                  href={event.registerButtonUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-block mt-4 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all"
                                >
                                  {event.registerButtonText}
                                </a>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!user) {
                                      navigate('/login', { state: { returnTo: event.registerButtonUrl! } });
                                    } else {
                                      navigate(event.registerButtonUrl!);
                                    }
                                  }}
                                  className="inline-block mt-4 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all"
                                >
                                  {event.registerButtonText}
                                </button>
                              )
                            )}
                          </div>

                          {/* Chevron */}
                          <ChevronRight className="h-6 w-6 text-orange-500 flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
